import {
  Socket as PhoenixSocket,
  Presence as PhoenixPresence,
  MessageRef,
} from 'phoenix'
import type { Channel, Push, Presence } from 'phoenix'

export interface ISocket {
  init: (
    url: string,
    options?: {
      params?: { [key: string]: unknown }
      timeout?: number
      transport?: any
    }
  ) => void
  onError: (callback: (callback: unknown) => void) => void
  channel: (topic: string, params?: { [key: string]: unknown }) => Channel
}

class Socket implements ISocket {
  private static instance: Socket
  private phoenixSocket: PhoenixSocket

  private constructor() {}

  public static getInstance(): Socket {
    if (!Socket.instance) {
      Socket.instance = new Socket()
    }
    return Socket.instance
  }

  public init(
    url: string,
    options?: {
      params?: { [key: string]: unknown }
      timeout?: number
      transport?: any
    }
  ) {
    if (!this.phoenixSocket) {
      try {
        const { params, timeout, transport } = options || {}
        // Check if we're in a Node.js environment and need to provide a transport
        const isNodeEnv =
          typeof window === 'undefined' && typeof global !== 'undefined'

        // In Node.js environment, make sure WebSocket is available globally through the polyfill
        if (isNodeEnv && (transport || global.WebSocket)) {
          console.log(
            'Using WebSocket polyfill for Node.js environment',
            transport ? 'with explicit transport' : 'with global.WebSocket'
          )
        }

        // Use the provided transport or default
        const socketOptions = {
          params,
          timeout,
          transport: transport || undefined,
        }

        this.phoenixSocket = new PhoenixSocket(url, socketOptions)
        this.phoenixSocket.connect()
      } catch (error) {
        console.error('Failed to create socket connection', error)
      }
    }
  }

  public onOpen = (callback: () => void | Promise<void>): MessageRef => {
    if (!this.phoenixSocket) {
      throw new Error('Socket must be initialized before onOpen is called')
    }
    return this.phoenixSocket.onOpen(callback)
  }

  public off = (refs: MessageRef[]) => {
    if (!this.phoenixSocket) {
      console.warn('Socket must be initialized before off is called')
      return
    }
    this.phoenixSocket.off(refs)
  }

  public disconnect = (cb: () => void) => {
    if (!this.phoenixSocket) {
      console.warn('Socket must be initialized before disconnect is called')
      // Invoke callback immediately to avoid hanging reconnect logic
      cb()
      return
    }
    const state = this.phoenixSocket.connectionState()
    if (state === 'open') {
      this.phoenixSocket.disconnect(cb)
    } else {
      // Already closed or errored, invoke callback immediately
      cb()
    }
  }

  public reconnect = (params: { token: string }) => {
    if (!this.phoenixSocket) {
      throw new Error('Socket must be initialized before reconnect is called')
    }
    this.phoenixSocket.connect(params)
  }

  public onError(callback: (callback: unknown) => void) {
    if (!this.phoenixSocket) {
      throw new Error('Socket must be initialized before onError is called')
    }
    this.phoenixSocket.onError(callback)
  }

  public channel(topic: string, params?: { [key: string]: unknown }): Channel {
    if (!this.phoenixSocket) {
      throw new Error('Socket must be initialized before channel is created')
    }

    return this.phoenixSocket.channel(topic, params)
  }
}

export { Channel, Push, Socket, Presence, PhoenixPresence }

export default Socket.getInstance()
